// src/components/ui/sections/TeamMembersList/TeamMembersListShadcn.jsx
import React, { useState } from 'react';
import {
  Users,
  Search,
  Filter,
  UserPlus,
  Crown,
  Shield,
  User,
  ChevronDown,
  Check
} from 'lucide-react';

// shadcn components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../card';
import { Button } from '../../button';
import { Input } from '../../input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../select';

// Import our custom components
import TeamMemberCardShadcn from '../../cards/TeamMemberCard/TeamMemberCardShadcn';
import InviteTeamMemberFormShadcn from '../../forms/InviteTeamMemberForm/InviteTeamMemberFormShadcn';

import { cn } from '../../../../utils/cn';

const TeamMembersListShadcn = ({
  teamMembers = [],
  pendingInvitations = [],
  selectedMemberId,
  onSelectMember,
  onInviteMember,
  onCancelInvitation,
  onResendInvitation,
  searchTerm,
  onSearchChange,
  roleFilter,
  onRoleFilterChange,
  activeStatusFilter = 'all',
  onActiveStatusFilterChange,
  loading = false,
  canInviteMembers = false,
  canManageInvitations = false
}) => {
  const [showInviteForm, setShowInviteForm] = useState(false);

  // Create unified list with pending invitations and team members
  const createUnifiedList = () => {
    const invitationItems = pendingInvitations.map(invitation => ({
      ...invitation,
      type: 'invitation'
    }));

    const memberItems = teamMembers.map(member => ({
      ...member,
      type: 'member'
    }));

    // Put pending invitations at the top
    return [...invitationItems, ...memberItems];
  };

  const unifiedList = createUnifiedList();

  // Handle invitation submission
  const handleInvite = async (email, role, message) => {
    try {
      await onInviteMember(email, role, message);
      setShowInviteForm(false); // Close modal on success
    } catch (error) {
      // Error handling is managed by TeamProvider
      console.error('Invitation failed:', error);
    }
  };

  // Role filter options - include pending invitations in counts
  const roleFilterOptions = [
    { value: 'all', label: 'All Roles', icon: Users, count: unifiedList.length },
    {
      value: 'admin',
      label: 'Administrators',
      icon: Crown,
      count: unifiedList.filter(item => item.role === 'admin').length
    },
    {
      value: 'manager',
      label: 'Managers',
      icon: Shield,
      count: unifiedList.filter(item => item.role === 'manager').length
    },
    {
      value: 'staff',
      label: 'Staff',
      icon: User,
      count: unifiedList.filter(item => item.role === 'staff').length
    }
  ];

  // Filter unified list based on search, role, and active status
  const filteredItems = unifiedList.filter(item => {
    // Handle search for both members and invitations
    const searchText = item.type === 'member'
      ? `${item.name} ${item.email}`.toLowerCase()
      : item.email.toLowerCase();

    const matchesSearch = !searchTerm ||
      searchText.includes(searchTerm.toLowerCase());

    const matchesRole = roleFilter === 'all' || item.role === roleFilter;

    // Active status filter (only applies to members, not invitations)
    const matchesActiveStatus = item.type === 'invitation' ||
      activeStatusFilter === 'all' ||
      (activeStatusFilter === 'active' && item.is_active) ||
      (activeStatusFilter === 'inactive' && !item.is_active);

    return matchesSearch && matchesRole && matchesActiveStatus;
  });

  return (
    <Card className="h-full flex flex-col">
      {/* Header */}
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Team Members
            </CardTitle>
            <CardDescription>
              Manage your team members and invitations
            </CardDescription>
          </div>

          {/* Invite Member Button */}
          {canInviteMembers && (
            <Button
              onClick={() => setShowInviteForm(true)}
              disabled={loading}
              size="sm"
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Invite
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search members by name or email..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Filters Row */}
        {unifiedList.length > 0 && (
          <div className="grid grid-cols-2 gap-2">
            {/* Role Filter */}
            <Select value={roleFilter} onValueChange={onRoleFilterChange}>
              <SelectTrigger>
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                {roleFilterOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        <span>{option.label} ({option.count})</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            {/* Active Status Filter */}
            <Select value={activeStatusFilter} onValueChange={onActiveStatusFilterChange}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  All ({teamMembers.length})
                </SelectItem>
                <SelectItem value="active">
                  Active ({teamMembers.filter(m => m.is_active).length})
                </SelectItem>
                <SelectItem value="inactive">
                  Inactive ({teamMembers.filter(m => !m.is_active).length})
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </CardHeader>

      {/* Team Members List */}
      <CardContent className="flex-1 overflow-y-auto space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-border border-t-primary rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-muted-foreground">Loading team members...</p>
            </div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              {searchTerm || roleFilter !== 'all'
                ? 'No members found'
                : 'No team members yet'
              }
            </h3>
            <p className="text-muted-foreground mb-6">
              {searchTerm || roleFilter !== 'all'
                ? 'Try adjusting your search or filter settings.'
                : 'Get started by inviting your first team member.'
              }
            </p>
            {canInviteMembers && !searchTerm && roleFilter === 'all' && (
              <Button
                onClick={() => setShowInviteForm(true)}
                size="default"
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Invite First Member
              </Button>
            )}
          </div>
        ) : (
          filteredItems.map((item) => (
            <TeamMemberCardShadcn
              key={`${item.type}-${item.id}`}
              member={item}
              type={item.type}
              isSelected={item.type === 'member' && selectedMemberId === item.id}
              onClick={item.type === 'member' ? () => onSelectMember(item.id) : undefined}
              onCancelInvitation={item.type === 'invitation' ? onCancelInvitation : undefined}
              onResendInvitation={item.type === 'invitation' ? onResendInvitation : undefined}
              canManageInvitations={canManageInvitations}
            />
          ))
        )}
      </CardContent>

      {/* Invite Form Modal */}
      <InviteTeamMemberFormShadcn
        isOpen={showInviteForm}
        onClose={() => setShowInviteForm(false)}
        onInvite={handleInvite}
        loading={loading}
      />
    </Card>
  );
};

export default TeamMembersListShadcn;